import { z } from "zod";

export const StudyStatus = z.enum(["draft", "active", "closed", "archived"]);
export type StudyStatus = z.infer<typeof StudyStatus>;

/** Tipo de un estímulo. Un estudio puede combinar imágenes y videos. */
export const StimulusKind = z.enum(["image", "video"]);
export type StimulusKind = z.infer<typeof StimulusKind>;

/** Composición de los estímulos activos de un estudio. */
export type StimulusMix = "image" | "video" | "mixed";

export function stimulusMix(kinds: ReadonlyArray<StimulusKind>): StimulusMix {
  const hasVideo = kinds.includes("video");
  const hasImage = kinds.includes("image");
  return hasVideo && hasImage ? "mixed" : hasVideo ? "video" : "image";
}

/** Estado del procesamiento de un estímulo (los videos se validan y convierten en segundo plano). */
export const ProcessingStatus = z.enum(["processing", "ready", "failed"]);
export type ProcessingStatus = z.infer<typeof ProcessingStatus>;

/** Origen de los datos de mirada. `synthetic` nunca se mezcla con `webgazer`. */
export const DataSource = z.enum(["webgazer", "synthetic"]);
export type DataSource = z.infer<typeof DataSource>;

export const SessionStatus = z.enum([
  "created",
  "calibrating",
  "running",
  "completed",
  "aborted",
  "interrupted",
]);
export type SessionStatus = z.infer<typeof SessionStatus>;

export const VisionCorrection = z.enum(["ninguno", "lentes", "contacto", "prefiero_no_decir"]);
export type VisionCorrection = z.infer<typeof VisionCorrection>;

export const SelfReportedVision = z.enum(["normal", "corregida", "dificultad", "prefiero_no_decir"]);
export type SelfReportedVision = z.infer<typeof SelfReportedVision>;

export const CalibrationPhase = z.enum(["initial", "revalidation"]);
export type CalibrationPhase = z.infer<typeof CalibrationPhase>;

export const QualityClass = z.enum(["pass", "warn", "fail"]);
export type QualityClass = z.infer<typeof QualityClass>;

/** Decisión tomada tras una validación (queda registrada). */
export const CalibrationDecision = z.enum(["continue", "recalibrate", "override", "cancel"]);
export type CalibrationDecision = z.infer<typeof CalibrationDecision>;

export const TrialStatus = z.enum(["completed", "interrupted", "discarded"]);
export type TrialStatus = z.infer<typeof TrialStatus>;

export const InvalidReason = z.enum([
  "null_coordinate",
  "non_finite",
  "out_of_viewport",
  "t_out_of_range",
]);
export type InvalidReason = z.infer<typeof InvalidReason>;

export const InterruptionReason = z.enum([
  "fullscreen_exit",
  "visibility_hidden",
  "window_blur",
  "viewport_resize",
  "device_pixel_ratio_change",
  "camera_lost",
  "permission_lost",
  "participant_cancel",
  "network_error",
  "validation_failed",
  "media_error",
  "playback_stalled",
  "autoplay_blocked",
  "other",
]);
export type InterruptionReason = z.infer<typeof InterruptionReason>;

export const VISION_CORRECTION_LABELS: Record<VisionCorrection, string> = {
  ninguno: "Ninguno",
  lentes: "Lentes (anteojos)",
  contacto: "Lentes de contacto",
  prefiero_no_decir: "Prefiero no decir",
};

export const SELF_REPORTED_VISION_LABELS: Record<SelfReportedVision, string> = {
  normal: "Normal",
  corregida: "Corregida (con lentes)",
  dificultad: "Tengo alguna dificultad visual",
  prefiero_no_decir: "Prefiero no decir",
};

export const INTERRUPTION_LABELS: Record<InterruptionReason, string> = {
  fullscreen_exit: "Se salió de pantalla completa",
  visibility_hidden: "La pestaña dejó de estar visible",
  window_blur: "La ventana perdió el foco",
  viewport_resize: "Cambió el tamaño de la ventana o el zoom",
  device_pixel_ratio_change: "Cambió la densidad de píxeles / zoom",
  camera_lost: "Se perdió la señal de la cámara",
  permission_lost: "Se revocó el permiso de cámara",
  participant_cancel: "La persona participante canceló",
  network_error: "Error de red persistente",
  validation_failed: "La validación no alcanzó la calidad mínima",
  media_error: "El video no se pudo reproducir",
  playback_stalled: "El video se detuvo durante la reproducción",
  autoplay_blocked: "El navegador bloqueó la reproducción del video",
  other: "Otro motivo",
};
